import { useEffect, useState, useCallback, RefObject } from 'react';

const clamp = (value: number): number => Math.max(Math.min(1, value), 0);

type CursorEvent = MouseEvent | TouchEvent;

interface CursorPositions {
  cursorX: number;
  cursorY: number;
}

const getCursorPositions = (e: CursorEvent): CursorPositions => ({
  cursorX: 'changedTouches' in e ? e.changedTouches[0].clientX : e.clientX,
  cursorY: 'changedTouches' in e ? e.changedTouches[0].clientY : e.clientY
});

interface UseSliderReturn {
  value: number;
  activate: () => void;
  activateKeys: () => void;
  deactivate: (e: CursorEvent) => void;
  deactivateKeys: () => void;
  isActive: boolean;
}

const useSlider = (trackRef: RefObject<HTMLElement>, initialValue?: number, isVertical?: boolean): UseSliderReturn => {
  const [value, setValue] = useState<number>(initialValue || 0);
  const [isActive, setIsActive] = useState<boolean>(false);

  const onMove = useCallback(
    (e: CursorEvent) => {
      e.preventDefault();
      const { cursorX, cursorY } = getCursorPositions(e);
      const track = trackRef.current;
      if (!track) return;

      const trackLength = isVertical ? track.offsetHeight : track.offsetWidth;
      const cursorDistancePxs = isVertical
        ? track.getBoundingClientRect().y - cursorY + trackLength
        : cursorX - track.getBoundingClientRect().x;

      const cursorDistanceDecimal = (1 / trackLength) * cursorDistancePxs;
      const cursorDistanceClamped = clamp(cursorDistanceDecimal);

      setValue(cursorDistanceClamped);
    },
    [trackRef, isVertical]
  );

  const onKeyDown = useCallback((e: KeyboardEvent) => {
    if ([37, 38, 39, 40].indexOf(e.keyCode) > -1) {
      e.preventDefault();
    }

    const inc = () => setValue((prevState = 0) => clamp(prevState + 0.05));
    const dec = () => setValue((prevState = 0) => clamp(prevState - 0.05));

    switch (e.keyCode) {
      case 39: // Right arrow
      case 38: // Up arrow
        return inc();
      case 37: // Left arrow
      case 40: // Down arrow
        return dec();
    }
  }, []);

  const activateKeys = useCallback(() => {
    window.addEventListener('keydown', onKeyDown);
  }, [onKeyDown]);

  const deactivateKeys = useCallback(() => {
    window.removeEventListener('keydown', onKeyDown);
  }, [onKeyDown]);

  const deactivate = useCallback(
    (e: CursorEvent) => {
      onMove(e); // Ensure movement in case user only clicks slider without moving the cursor
      window.removeEventListener('mousemove', onMove as EventListener);
      window.removeEventListener('touchmove', onMove as EventListener);
      window.removeEventListener('mouseup', deactivate as EventListener);
      window.removeEventListener('touchend', deactivate as EventListener);
      window.removeEventListener('keydown', onKeyDown);

      setIsActive(false);
    },
    [onMove, onKeyDown]
  );

  const activate = useCallback(() => {
    window.addEventListener('mousemove', onMove as EventListener);
    window.addEventListener('touchmove', onMove as EventListener, { passive: false }); // Passive prevents scrolling
    window.addEventListener('mouseup', deactivate as EventListener);
    window.addEventListener('touchend', deactivate as EventListener);
    window.addEventListener('keydown', onKeyDown);

    setIsActive(true);
  }, [onMove, deactivate, onKeyDown]);

  useEffect(() => {
    return () => {
      window.removeEventListener('mousemove', onMove as EventListener);
      window.removeEventListener('touchmove', onMove as EventListener);
      window.removeEventListener('mouseup', deactivate as EventListener);
      window.removeEventListener('touchend', deactivate as EventListener);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onMove, deactivate, onKeyDown]);

  return {
    value,
    activate,
    activateKeys,
    deactivate,
    deactivateKeys,
    isActive
  };
};

export default useSlider;
